import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import {
  DOSAGE_AUTOFILL_FIELDS,
  DosageAutofillField,
  DosageAutofillNotice,
  DosageAutofillPick,
  DosagePrefill,
  autofillUpdates,
  drugDataPrefill,
  historyPrefill,
  isDosageAutofillField,
} from './dosageAutofill';
import {
  DOSING_SUGGESTION_STALE_MS,
  DosingSuggestionRequest,
  dosingSuggestionKey,
  fetchDosingSuggestion,
} from './medicationDosingSuggestion';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';

export interface DosageAutofill {
  /** Where the prefilled dose came from; null while nothing is prefilled. */
  notice: DosageAutofillNotice | null;
  /**
   * An explicit pick in step 1: take back what the last pick filled, fill
   * from this drug's data, then from how the clinic last prescribed it.
   */
  applyPick: (pick: DosageAutofillPick) => void;
  /** "Wyczyść podpowiedź", or a name typed over: take back what was filled and still stands. */
  clear: () => void;
}

interface DosageAutofillArgs {
  methods: UseFormReturn<MedicationsFormModalValuesProps>;
  /** Closing the modal forgets the notice and any answer still on its way. */
  isOpen: boolean;
  /** The patient, for the clinic's history; without one only the drug's data fills. */
  patientId?: string;
  /** The host holds the form (a save in flight, unconfirmed, read-only). */
  disabled?: boolean;
}

const landedAnything = (prefill: DosagePrefill): boolean =>
  Object.keys(prefill).length > 0;

/**
 * #692: the dosage step's autofill. Only an explicit pick runs it — loading
 * a saved Medication never does — and it only fills fields that are empty
 * and that the vet has not edited in this form session (R1-1: not even back
 * to empty). The history is read under an organization-scoped key; a
 * failed read fills nothing and says nothing, and an answer that arrives
 * once the dose is "do ustalenia" or the form is held lands nowhere.
 */
export function useDosageAutofill({
  methods,
  isOpen,
  patientId,
  disabled = false,
}: DosageAutofillArgs): DosageAutofill {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganizationContext();
  const organizationId = selectedOrganization?.id;
  const { getValues, getFieldState, setValue, watch } = methods;
  const [notice, setNotice] = useState<DosageAutofillNotice | null>(null);
  /** What the autofill wrote, so "Wyczyść podpowiedź" takes back exactly that. */
  const applied = useRef<DosagePrefill>({});
  /** Bumped by every pick, clear and close: an older answer lands nowhere. */
  const generation = useRef(0);
  /**
   * R1-1: the fields the vet has edited since the modal opened. RHF's
   * `isDirty` compares with the default, so it forgets an edit taken back to
   * empty; a field's own change event is the vet's alone.
   */
  const editedByVet = useRef(new Set<DosageAutofillField>());
  /**
   * Set while the autofill writes. RHF 7.65 re-sends a setValue on a watched
   * field (the stock default and the dosage selects watch these) with the
   * whole form state, which keeps the `type` of the last change event of any
   * field — the pick in step 1, say — so our own write read as the vet's.
   */
  const writing = useRef(false);
  /** Read when the history answers, which may be after a save has started. */
  const held = useRef(disabled);
  useEffect(() => {
    held.current = disabled;
  }, [disabled]);

  useEffect(() => {
    const subscription = watch((_, { name, type }) => {
      if (
        !writing.current &&
        type === 'change' &&
        isDosageAutofillField(name)
      ) {
        editedByVet.current.add(name);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // A dirty field is the vet's too: a form reopened on an unconfirmed save's
  // values (R3-4) differs from its defaults by what the vet typed then.
  const wasEdited = useCallback(
    (field: DosageAutofillField): boolean =>
      editedByVet.current.has(field) || getFieldState(field).isDirty,
    [getFieldState],
  );

  const write = useCallback(
    (
      field: DosageAutofillField,
      value: MedicationsFormModalValuesProps[DosageAutofillField],
    ): void => {
      writing.current = true;
      try {
        setValue(field, value);
      } finally {
        writing.current = false;
      }
    },
    [setValue],
  );

  const land = useCallback(
    (prefill: DosagePrefill): DosagePrefill => {
      const updates = autofillUpdates(prefill, (field) => ({
        value: getValues(field),
        edited: wasEdited(field),
      }));
      DOSAGE_AUTOFILL_FIELDS.forEach((field) => {
        const value = updates[field];
        if (value !== undefined) write(field, value);
      });
      applied.current = { ...applied.current, ...updates };
      return updates;
    },
    [getValues, wasEdited, write],
  );

  const forget = useCallback((): void => {
    generation.current += 1;
    applied.current = {};
    setNotice(null);
  }, []);

  // A field the vet has edited since stays, even set back to the value it
  // was given.
  const clear = useCallback((): void => {
    DOSAGE_AUTOFILL_FIELDS.forEach((field) => {
      const value = applied.current[field];
      if (
        value !== undefined &&
        !wasEdited(field) &&
        getValues(field) === value
      ) {
        write(field, '');
      }
    });
    forget();
  }, [forget, getValues, wasEdited, write]);

  const applyPick = useCallback(
    (pick: DosageAutofillPick): void => {
      clear();
      const pickGeneration = generation.current;
      if (landedAnything(land(drugDataPrefill(pick)))) {
        setNotice({ kind: 'DRUG' });
      }
      if (!patientId || !organizationId) return;
      const request: DosingSuggestionRequest = {
        organizationId,
        profileId: patientId,
        medicineProductId: pick.medicineProductId,
        name: pick.name,
      };
      queryClient
        .fetchQuery({
          queryKey: dosingSuggestionKey(request),
          queryFn: ({ signal }) => fetchDosingSuggestion(request, signal),
          staleTime: DOSING_SUGGESTION_STALE_MS,
          retry: false,
        })
        .then((suggestion) => {
          if (!suggestion || pickGeneration !== generation.current) return;
          if (held.current || getValues('dosagePending')) return;
          if (landedAnything(land(historyPrefill(suggestion)))) {
            setNotice({
              kind: 'HISTORY',
              prescribedAt: suggestion.prescribedAt,
              petCategory: suggestion.petCategory,
              sameSpecies: suggestion.sameSpecies,
            });
          }
        })
        .catch(() => undefined);
    },
    [clear, getValues, land, organizationId, patientId, queryClient],
  );

  useEffect(() => {
    if (isOpen) return;
    forget();
    editedByVet.current.clear();
  }, [forget, isOpen]);

  return { notice, applyPick, clear };
}
